import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import {
    TextField,
    Tooltip,
    IconButton,
    InputAdornment,
    Box,
} from '@mui/material';
import Draggable from 'react-draggable';
import { styled } from '@mui/system';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import Iframe from 'react-iframe';
import NoDocumentsPdf from '../../../assests/pdf/NoDocument.pdf'; // adjust path as needed
import { Snackbar, Alert, Button } from '@mui/material';
import { beautifyNumber } from '../../../Utils/FormatNumber';
const StyledContainer = styled('div')({
    position: 'absolute',
    top: '10%',
    left: '20%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    minHeight: '40vh',
    maxHeight: '85vh',
    overflowY: 'auto',
    minWidth: '500px',
    zIndex: 1300,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.4)',
    border: '1px solid #304fff',
});

function AddItemsModal({
    open,
    onClose,
    onSave,
    existingItems = [],
    items = [],
    doc,
    pendingAction,
    headerNote = ''
}) {
    const { t, i18n } = useTranslation();
    const nodeRef = useRef(null);
    const [direction, setDirection] = useState('rtl');
    const [itemsToAdd, setItemsToAdd] = useState([]);
    const [total, setTotal] = useState(0);
    const [showError, setShowError] = useState(false);
    const [errorExceedsAmount, setErrorExceedsAmount] = useState(false);
    const [customError, setCustomError] = useState('');
    useEffect(() => {
        const calculatedTotal = itemsToAdd.reduce((acc, item) => {
            const quantity = parseFloat(item.quantity) || 1;
            const price = parseFloat(item.price) || 0;
            const discount = parseFloat(item.discount) || 0;
            const discountedPrice = price * quantity * (1 - discount / 100);
            return acc + discountedPrice;
        }, 0);

        
        setTotal(calculatedTotal);

        const hasMismatch = doc?.amount != null && Math.abs(calculatedTotal -  Math.abs(doc.amount)) > 0.01; // small epsilon for decimals

        setShowError(hasMismatch);
        setErrorExceedsAmount(hasMismatch); // no need to duplicate the condition separately

    }, [itemsToAdd, doc]);

    useEffect(() => {
        setDirection(['he', 'ar'].includes(i18n.language) ? 'rtl' : 'ltr');
    }, [i18n.language]);




    useEffect(() => {
        if (items) {
            const allMakats = items.map((it) => parseInt(it.makat, 10) || 0);
            const maxMakat = allMakats.length > 0 ? Math.max(...allMakats) : 0;
            const newMakat = (maxMakat + 1).toString();
            setItemsToAdd([{
                isNew: true,
                makat: newMakat,
                name: '',
                price: '',
                discount: 0,
                quantity: 1,
                itemId: null,
            }]);
        }
    }, [items]);

    const selectOptions = items.map(ci => ({
        value: ci.makat,
        label: ci.name,
        price: ci.price,
        itemId: ci.id,
    }));

    const inputPropsStyle = {
        disableUnderline: true,
        className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
        sx: {
            height: '48px',
            padding: '0 10px',
            borderRadius: '8px',
            background: '#F5F5F6',
            border: '1px solid transparent',
            '&:focus-within': {
                border: '2px solid #304FFF',
            },
        },
    };

    const inputStyle = {
        style: {
            textAlign: direction === 'rtl' ? 'right' : 'left',
            direction: direction === 'rtl' ? 'rtl' : 'ltr',
        },
    };

    const handleAddRow = () => {
        const allMakats = [
            ...existingItems.map((it) => parseInt(it.makat, 10) || 0),
            ...itemsToAdd.map((it) => parseInt(it.makat, 10) || 0),
        ];
        const maxMakat = allMakats.length > 0 ? Math.max(...allMakats) : 0;
        const newMakat = (maxMakat + 1).toString();

        setItemsToAdd(prev => [...prev, {
            isNew: true,
            makat: newMakat,
            name: '',
            price: '',
            discount: 0,
            quantity: 1,
            itemId: null,
        }]);
    };

    const handleRemoveRow = (index) => {
        setItemsToAdd(prev => prev.filter((_, i) => i !== index));
    };

    const validateItems = () => {
        for (const item of itemsToAdd) {
            if (!item.name?.trim()) {
                return { valid: false, message: t('ItemNameRequired') };
            }
            const price = parseFloat(item.price);
            if (isNaN(price) || price <= 0) {
                return { valid: false, message: t('ItemPriceMustBeGreaterThanZero') };
            }
        }
        return { valid: true };
    };


    const handleSelectChange = (index, selectedOption) => {
        setItemsToAdd(prev => {
            const updated = [...prev];
            if (!selectedOption || selectedOption.value === 'add-item') {
                updated[index] = {
                    isNew: true,
                    makat: '',
                    name: '',
                    price: '',
                    discount: 0,
                    quantity: 1,
                    itemId: null,
                };
            } else {
                updated[index] = {
                    isNew: false,
                    makat: selectedOption.value,
                    name: selectedOption.label,
                    price: selectedOption.price || '',
                    discount: 0,
                    quantity: 1,
                    itemId: selectedOption.itemId || null,
                };
            }
            return updated;
        });
    };

    const handleChange = (index, field, value) => {
        setItemsToAdd(prev => {
            const updated = [...prev];
            updated[index][field] = value;
            return updated;
        });
    };


    const handleSave = () => {
        const validation = validateItems();
        if (!validation.valid) {
            setCustomError(validation.message); // <-- dynamic error
            return;
        }

        if (errorExceedsAmount) {
            setCustomError(t('TotalItemsAmountExceedsDocumentAmount')); // <-- dynamic error
            return;
        }

        onSave(itemsToAdd, pendingAction, doc);
    };

    if (!open) return null;

    return (
        <Draggable handle=".handle" nodeRef={nodeRef}>
            <StyledContainer ref={nodeRef} style={{ direction }}>
                {/* Header */}
                <div
                    className="handle"
                    style={{
                        cursor: 'move',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Tooltip title={t('Exit')}>
                        <IconButton onClick={onClose}>
                            <CancelIcon style={{ color: '#304FFF' }} />
                        </IconButton>
                    </Tooltip>
                    <div className="edit-title">
                        {t('AddClientItem')}
                    </div>
                    <div />
                </div>

                {headerNote && (
                    <div
                        style={{
                            marginBottom: '12px',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#304FFF',
                            textAlign: direction === 'rtl' ? 'right' : 'left',
                        }}
                    >
                        {headerNote}
                    </div>
                )}

                {/* Document URL + Preview */}
                {doc?.documentUrl && (
                    <>

                        <Box
                            sx={{
                                borderRadius: 2,
                                border: '1px solid #E0E0E0',
                                overflow: 'hidden',
                                boxShadow: 1,
                                mb: 3,
                            }}
                        >
                            <Iframe
                                url={doc.documentUrl ? `${doc.documentUrl}#page=1` : `${NoDocumentsPdf}#page=1`}
                                width="100%"
                                height="400px"
                                style={{ border: 'none' }}
                                title="PDF Preview"
                            />
                        </Box>
                    </>
                )}

                {/* Item Fields */}
                {itemsToAdd.map((item, idx) => (
                    <div key={idx} style={{ marginBottom: 24 }}>
                        {idx > 0 && <hr style={{ border: '0.5px solid #ddd', marginBottom: 24 }} />}
                        <div style={{ marginBottom: 16 }}>
                            <Select
                                value={
                                    item.isNew
                                        ? { value: 'add-item', label: t('NewItem') }
                                        : { value: item.makat, label: item.name }
                                }
                                onChange={(option) => handleSelectChange(idx, option)}
                                options={selectOptions}
                            />
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            <TextField label={t('Makat')} variant="standard" style={{ width: '120px' }}
                                value={item.makat} onChange={(e) => handleChange(idx, 'makat', e.target.value)}
                                InputProps={inputPropsStyle} inputProps={inputStyle} InputLabelProps={{ shrink: true }} />
                            <TextField label={t('Name')} variant="standard" style={{ width: '120px' }}
                                value={item.name} onChange={(e) => handleChange(idx, 'name', e.target.value)}
                                InputProps={inputPropsStyle} inputProps={inputStyle} InputLabelProps={{ shrink: true }} />
                            <TextField type="number" label={t('Price')} variant="standard" style={{ width: '90px' }}
                                value={item.price} onChange={(e) => handleChange(idx, 'price', e.target.value)}
                                InputProps={inputPropsStyle} inputProps={inputStyle} InputLabelProps={{ shrink: true }} />
                            <TextField type="number" label={t('Discount') + ' (%)'} variant="standard" style={{ width: '90px' }}
                                value={item.discount} onChange={(e) => handleChange(idx, 'discount', e.target.value)}
                                InputProps={inputPropsStyle} inputProps={{ ...inputStyle.style, min: 0, max: 100 }}
                                InputLabelProps={{ shrink: true }} />
                            <TextField type="number" label={t('Quantity')} variant="standard" style={{ width: '90px' }}
                                value={item.quantity} onChange={(e) => handleChange(idx, 'quantity', e.target.value)}
                                InputProps={inputPropsStyle} inputProps={{ ...inputStyle.style, min: 1 }}
                                InputLabelProps={{ shrink: true }} />
                            {itemsToAdd.length > 1 && (
                                <Tooltip title={t('RemoveItem')}>
                                    <IconButton onClick={() => handleRemoveRow(idx)}>
                                        <DeleteIcon style={{ color: 'red' }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                ))}

                <div
                    onClick={handleAddRow}
                    style={{
                        alignSelf: direction === 'rtl' ? 'flex-start' : 'flex-end',
                        marginBottom: 16,
                        cursor: 'pointer',
                        padding: '6px 16px',
                        backgroundColor: '#304FFF',
                        color: '#fff',
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 500,
                        width: 'fit-content',
                    }}
                >
                    {t('AddItem')}
                </div>

                {/* Footer Actions */}
                {/* <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 16, gap: '10px' }}>
                    <div onClick={onClose} className="edit-cancel-button">
                        {t('Cancel')}
                    </div>
                    <div onClick={handleSave} className="edit-save-button">
                        {t('Save')}
                    </div>
                </div> */}
                <Box sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
                    {t('Total')}:&nbsp;
                    <span style={{ color: showError ? '#d32f2f' : '#1976d2' }}>
                        {beautifyNumber(total)}
                    </span>
                    {doc?.amount != null && (
                        <>&nbsp;/&nbsp;{Math.abs(doc.amount)}</>
                    )}
                </Box>

                {/* buttons */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button variant="outlined" color="inherit" onClick={onClose}>
                        {t('Cancel')}
                    </Button>

                    <Tooltip
                        title={showError ? t('TotalItemsAmountExceedsDocumentAmount') : ''}
                        disableHoverListener={!showError}
                    >
                        <span> {/* needed because a disabled <Button> loses pointer-events */}
                            <Button
                                variant="contained"
                                color="primary"
                                // disabled={showError}
                                onClick={handleSave}
                            >
                                {t('Save')}
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
                <Snackbar
                    open={!!customError}
                    onClose={() => setCustomError('')}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    autoHideDuration={4000}
                >
                    <Alert
                        severity="error"
                        variant="filled"
                        sx={{ width: '100%' }}
                        onClose={() => setCustomError('')}
                    >
                        {customError}
                    </Alert>
                </Snackbar>
            </StyledContainer>
        </Draggable>
    );
}

export default AddItemsModal;
